import { IFileResponse } from '@lib/core';
import { IInvoiceGroupItem } from './InvoiceItemGroup';
import {
    IInvoiceAddressDto,
    IInvoiceItemOrderQuantitiesViewDto,
    IStandaloneInvoiceItemAccountingDto,
} from './IStandaloneInvoiceDetail';
import { AccountingSelectionInput, CustomAccountingInput } from '@lib/accounting/types';
import { ICompanyAddressDto } from '@lib/address';
import { InvoiceStatus } from '@lib/invoice-public';
import { CreateInvoiceCharge } from '../../create-standalone/types/StandaloneInvoiceDraft';
import { InvoicePossibleActionType } from './InvoicePossibleActionType';
import { LinkedDocument } from './LinkedDocument';
import { InvoiceDocumentType } from '../../types/InvoiceDocumentType';

export interface IInvoiceDetail {
    id: string;
    invoiceNumber: string;
    companyCodeId?: string;
    supplierName: string;
    supplierNumber?: string | null;
    supplierAddress: ICompanyAddressDto;
    invoiceAddress: IInvoiceAddressDto;
    bookingDate: string;
    documentDate: string;
    comment: string;
    status: InvoiceStatus; // invoice status enum
    documentType?: InvoiceDocumentType;
    files: IFileResponse[]; // FileDto
    items: IInvoiceDetailItem[];
    price: IInvoiceDetailPrice;
    paymentUntil: string;
    paymentDate: string;
    charges: CreateInvoiceCharge[];
    possibleActions: InvoicePossibleActionType[];
    linkedDocuments: LinkedDocument[];
}

export interface IInvoiceDetailPrice {
    totalNetPrice: number | null;
    totalGrossPrice: number | null;
    taxes: IInvoiceDetailPriceTax[] | null;
    currency: string | null;
}

export interface IInvoiceDetailPriceTax {
    percent: number;
    amount: number;
}

export interface IInvoiceDetailItem extends IInvoiceGroupItem {
    invoicePosition: number;
    orderPosition: number | null;
    receiptPosition: number | null;
    name: string;
    description: string;
    articleNumberSupplier: string;
    price: IInvoiceDetailItemPrice;
    quantity: number;
    orderUnit: string;
    /** Optional discount percentage (0-100) applied to this item */
    discountPercentage?: number;
    /** Unit count the price refers to. Defaults to 1. */
    priceQuantity?: number;
    materialGroupCode?: string | null;
    /** Counters of the linked order item; absent for items without a readable order relation. */
    orderItemQuantities?: IInvoiceItemOrderQuantitiesViewDto | null;
}

export interface IInvoiceDetailItemPrice {
    unitPrice: number;
    linePrice: number | null;
    currency: string | null;
    taxes: IInvoiceDetailPriceTax;
}

export class InvoiceDetailItem implements IInvoiceDetailItem {
    articleNumberSupplier: string;
    description: string;
    invoicePosition: number;
    itemId: string;
    name: string;
    orderId: string | null;
    orderErpNumber?: string | null;
    orderPosition: number | null;
    orderUnit: string;
    price: IInvoiceDetailItemPrice;
    quantity: number;
    receiptId: number | null;
    receiptNumber: string | null;
    receiptPosition: number | null;
    discountPercentage?: number;
    priceQuantity?: number;
    materialGroupCode?: string | null;
    orderItemQuantities?: IInvoiceItemOrderQuantitiesViewDto | null;

    accountingInput: AccountingSelectionInput | undefined;

    constructor(dto: IInvoiceDetailItem, accountingInput?: AccountingSelectionInput) {
        this.articleNumberSupplier = dto.articleNumberSupplier;
        this.description = dto.description;
        this.invoicePosition = dto.invoicePosition;
        this.itemId = dto.itemId;
        this.name = dto.name;
        this.orderId = dto.orderId;
        this.orderErpNumber = dto.orderErpNumber;
        this.orderPosition = dto.orderPosition;
        this.orderUnit = dto.orderUnit;
        this.price = dto.price;
        this.quantity = dto.quantity;
        this.receiptId = dto.receiptId;
        this.receiptNumber = dto.receiptNumber;
        this.receiptPosition = dto.receiptPosition;
        this.discountPercentage = dto.discountPercentage;
        this.priceQuantity = dto.priceQuantity;
        this.materialGroupCode = dto.materialGroupCode;
        this.orderItemQuantities = dto.orderItemQuantities;
        if (accountingInput) {
            this.accountingInput = accountingInput;
        }
    }

    setAccounting(input: IStandaloneInvoiceItemAccountingDto): void {
        const custom: CustomAccountingInput[] = input.customAccountings.map(
            (c) => new CustomAccountingInput(c.type, c.code),
        );
        this.accountingInput = new AccountingSelectionInput(
            input.costCenter,
            input.generalLedgerAccount,
            input.project,
            custom,
        );
    }

    clone(): InvoiceDetailItem {
        return new InvoiceDetailItem(this, this.accountingInput);
    }
}
